export interface URLBuilderSeatAttendee {
  firstName: string;
  lastName: string;
  email: string;
  customInputs: Record<string, string>;
}

interface AttendeeCustomInput {
  id: string;
  title?: string;
}

interface URLBuilderTicketConfiguration {
  type?: 'SINGLE' | 'GROUP';
  ticketType?: 'INDIVIDUAL' | 'GROUP';
  groupSizeMode?: 'FIXED' | 'MINIMUM';
  groupSize?: number;
  seats?: number;
  minimumSeats?: number;
}

const normalizeParamTitle = (title: string): string =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const getURLBuilderConfiguredSeatCount = (
  ticket?: URLBuilderTicketConfiguration | null,
): number => {
  if (!ticket) return 0;
  const isGroup = ticket.type === 'GROUP' || ticket.ticketType === 'GROUP';
  if (!isGroup) return 1;
  const value =
    ticket.groupSizeMode === 'MINIMUM' ? ticket.minimumSeats : (ticket.groupSize ?? ticket.seats);
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * Resolve a saved link's `ticket` name the way the buyer page does
 * (TicketSelectionForm): exact match first, then case-insensitive. Picking a
 * different ticket here would rewrite the link to another ticket on save.
 */
export const findURLBuilderTicketByName = <T extends { name: string }>(
  tickets: T[],
  name: string,
): T | undefined =>
  tickets.find((ticket) => ticket.name === name) ??
  tickets.find((ticket) => ticket.name.toLowerCase() === name.toLowerCase());

const isURLBuilderGroupTicket =(ticket: URLBuilderTicketConfiguration): boolean =>
  ticket.type === 'GROUP' || ticket.ticketType === 'GROUP';

const parsePositiveSeatCount = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Seat rows to restore when editing a saved link. `ticketseats` carries the
 * requested count for MINIMUM groups (written here) and for Individual tickets
 * (written by the public API's `prefill.ticketQuantity`); ignoring it for an
 * Individual ticket would shrink a multi-ticket link to one ticket on save.
 */
export const getURLBuilderSavedSeatCount = (
  ticket: URLBuilderTicketConfiguration & { minimumSeats?: number },
  prefill: Record<string, string>,
): number => {
  if (ticket.groupSizeMode === 'MINIMUM') {
    return Number(prefill.ticketseats || ticket.minimumSeats || 0);
  }
  if (!isURLBuilderGroupTicket(ticket)) {
    return parsePositiveSeatCount(prefill.ticketseats) ?? 1;
  }
  return getURLBuilderConfiguredSeatCount(ticket);
};

/** Whether a saved link must carry `ticketseats` for this ticket and seat count. */
export const shouldPersistURLBuilderTicketSeats = (
  ticket: URLBuilderTicketConfiguration,
  seatCount: number,
): boolean =>
  ticket.groupSizeMode === 'MINIMUM' || (!isURLBuilderGroupTicket(ticket) && seatCount > 1);

/** Restores the requested seat count even when attendee identity fields are blank. */
export const restoreURLBuilderSeatAttendees = (
  prefill: Record<string, string>,
  requestedSeatCount: number,
  formCustomInputs: AttendeeCustomInput[],
): URLBuilderSeatAttendee[] =>
  Array.from({ length: Math.max(0, requestedSeatCount) }, (_, index) => {
    const seatNum = index + 1;
    const customInputs: Record<string, string> = {};
    const seatPrefix = `seat${seatNum}_ci_`;
    Object.entries(prefill).forEach(([key, value]) => {
      if (!key.startsWith(seatPrefix) || !value) return;
      const paramTitle = key.slice(seatPrefix.length);
      const matchedInput = formCustomInputs.find(
        (input) => normalizeParamTitle(input.title || '') === paramTitle,
      );
      if (matchedInput) customInputs[matchedInput.id] = value;
    });
    return {
      firstName: prefill[`seat${seatNum}_firstname`] || '',
      lastName: prefill[`seat${seatNum}_lastname`] || '',
      email: prefill[`seat${seatNum}_email`] || '',
      customInputs,
    };
  });
